import React, { useState } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Container, 
  Grid, 
  Paper, 
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  CardActions,
  IconButton as MuiIconButton,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Fab,
  Tooltip,
  ListItemButton
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Support as SupportIcon,
  QuestionAnswer as QuestionAnswerIcon,
  ShoppingCart as ShoppingCartIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  BugReport as BugReportIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Star as StarIcon,
  People as PeopleIcon,
  PendingActions as PendingActionsIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Language as LanguageIcon,
  Add as AddIcon,
  PersonSearch as PersonSearchIcon,
  ShoppingBasket as ShoppingBasketIcon,
  Store as StoreIcon,
  LocationOn as LocationOnIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExitToApp as ExitToAppIcon,
  Timeline as TimelineIcon,
  Today as TodayIcon,
  Backspace as BackspaceIcon,
  Mic as MicIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';

// Import support dashboard pages (these would need to be created)
import SupportTickets from './Tickets';
import CustomerQueries from './CustomerQueries';
import OrderSupport from './OrderSupport';
import VoiceOrder from './VoiceOrder';
import TechnicalSupport from './TechnicalSupport';
import DeliverySupport from './DeliverySupport';
import PaymentSupport from './PaymentSupport';
import SupportAnalytics from './Analytics';
import SupportSettings from './Settings';
import AsteriskCallManager from '../../components/call/AsteriskCallManager';

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  position: 'fixed',
  zIndex: theme.zIndex.drawer + 1,
}));

const SupportDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [supportMenuAnchor, setSupportMenuAnchor] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [isHoveringSupport, setIsHoveringSupport] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [language, setLanguage] = useState('bn'); // 'bn' for Bangla, 'en' for English
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  
  // Add all dialog and order state management here
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [customerSearchDialogOpen, setCustomerSearchDialogOpen] = useState(false);
  const [customerProfileDialogOpen, setCustomerProfileDialogOpen] = useState(false);
  const [foundCustomer, setFoundCustomer] = useState(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchType, setCustomerSearchType] = useState('phone');
  const [deliveryStatusDialogOpen, setDeliveryStatusDialogOpen] = useState(false);
  const [returnProcessDialogOpen, setReturnProcessDialogOpen] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [phoneCallDialogOpen, setPhoneCallDialogOpen] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedUpazilla, setSelectedUpazilla] = useState('');
  const [selectedBazar, setSelectedBazar] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState('');
  const [selectedDeliveryPartner, setSelectedDeliveryPartner] = useState('Grameen Delivery');
  const [orderItems, setOrderItems] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [orderStep, setOrderStep] = useState(1);
  
  // Enhanced order dialog state
  const [isGrameenProduct, setIsGrameenProduct] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cod');
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('home_delivery');
  const [deliveryAddress, setDeliveryAddress] = useState('');

  // Phone dialer state
  const [dialerNumber, setDialerNumber] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    handleLanguageMenuClose();
  };

  const handleSupportMenuOpen = (event) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsHoveringSupport(true);
    setSupportMenuAnchor(event.currentTarget);
  };

  const handleSupportMenuClose = () => {
    setIsHoveringSupport(false);
    setSupportMenuAnchor(null);
  };

  const handleSupportMenuEnter = (event) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    
    // Only open if not already hovering (prevents repeated opening)
    if (!isHoveringSupport) {
      setIsHoveringSupport(true);
      setSupportMenuAnchor(event.currentTarget);
    }
  };

  const handleSupportMenuLeave = () => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    
    // Set a longer timeout to close the menu after leaving
    const timeout = setTimeout(() => {
      if (!isHoveringSupport) {
        setSupportMenuAnchor(null);
      }
    }, 300);
    setHoverTimeout(timeout);
  };

  const handleDropdownEnter = () => {
    // Clear the timeout when hovering over the dropdown
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsHoveringSupport(true);
  };

  const handleDropdownLeave = () => {
    setIsHoveringSupport(false);
    // Set a timeout to close the menu when leaving the dropdown
    const timeout = setTimeout(() => {
      setSupportMenuAnchor(null);
    }, 200);
    setHoverTimeout(timeout);
  };

  const handleLogout = () => {
    handleMenuClose();
    // In a real app, this would handle logout logic
    navigate('/login');
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleMobileNavigation = (path) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const handleMobileTabChange = (newValue) => {
    setCurrentTab(newValue);
    setMobileDrawerOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    // Don't navigate if it's the Support tab (index 1) - hover will handle dropdown
    if (newValue === 1) {
      return;
    }
    setCurrentTab(newValue);
    navigate(menuItems[newValue].path);
  };

  const handleSupportNavigation = (path) => {
    navigate(path);
    handleSupportMenuClose();
    // Set current tab to Support when navigating to any support page
    setCurrentTab(1);
  };

  // Translations
  const translations = {
    bn: {
      title: 'সাপোর্ট ড্যাশবোর্ড',
      dashboard: 'ড্যাশবোর্ড',
      support: 'সাপোর্ট',
      tickets: 'টিকেট',
      analytics: 'বিশ্লেষণ',
      settings: 'সেটিংস',
      profile: 'প্রোফাইল',
      logout: 'লগআউট',
      customerQueries: 'গ্রাহক জিজ্ঞাসা',
      orderSupport: 'অর্ডার সাপোর্ট',
      voiceOrder: 'ভয়েস অর্ডার',
      technicalSupport: 'টেকনিক্যাল সাপোর্ট',
      deliverySupport: 'ডেলিভারি সাপোর্ট',
      paymentSupport: 'পেমেন্ট সাপোর্ট',
      language: 'ভাষা',
      bangla: 'বাংলা',
      english: 'English'
    },
    en: {
      title: 'Support Dashboard',
      dashboard: 'Dashboard',
      support: 'Support',
      tickets: 'Tickets',
      analytics: 'Analytics',
      settings: 'Settings',
      profile: 'Profile',
      logout: 'Logout',
      customerQueries: 'Customer Queries',
      orderSupport: 'Order Support',
      voiceOrder: 'Voice Order',
      technicalSupport: 'Technical Support',
      deliverySupport: 'Delivery Support',
      paymentSupport: 'Payment Support',
      language: 'Language',
      bangla: 'বাংলা',
      english: 'English'
    }
  };

  const t = translations[language];

  const menuItems = [
    { text: t.dashboard, icon: <DashboardIcon />, path: '/support/dashboard' },
    { text: t.support, icon: <SupportIcon />, path: '/support/dashboard/support', hasDropdown: true },
    { text: t.tickets, icon: <QuestionAnswerIcon />, path: '/support/dashboard/tickets' },
    { text: t.analytics, icon: <BarChartIcon />, path: '/support/dashboard/analytics' },
    { text: t.settings, icon: <SettingsIcon />, path: '/support/dashboard/settings' },
  ];

  const supportMenuItems = [
    { text: t.customerQueries, path: '/support/dashboard/support/customer-queries', icon: <QuestionAnswerIcon /> },
    { text: t.orderSupport, path: '/support/dashboard/support/orders', icon: <ShoppingCartIcon /> },
    { text: t.voiceOrder, path: '/support/dashboard/support/voice-order', icon: <MicIcon /> },
    { text: t.technicalSupport, path: '/support/dashboard/support/technical', icon: <BugReportIcon /> },
    { text: t.deliverySupport, path: '/support/dashboard/support/delivery', icon: <LocalShippingIcon /> },
    { text: t.paymentSupport, path: '/support/dashboard/support/payment', icon: <PaymentIcon /> },
  ];

  // Set current tab based on current path
  React.useEffect(() => {
    const currentIndex = menuItems.findIndex(item => {
      if (item.hasDropdown && location.pathname.includes('/support')) {
        return true;
      }
      return item.path === location.pathname;
    });
    if (currentIndex !== -1) {
      setCurrentTab(currentIndex);
    }
  }, [location.pathname]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  // Mock customer database
  const mockCustomers = [
    {
      id: 'CUST001',
      name: 'Jewel',
      phone: '0172222222222',
      email: 'jewel@example.com',
      address: 'বাড়ি নং ১২ৃ, ধানমন্ডি, ঢাকা',
      district: 'ঢাকা',
      upazilla: 'ধানমন্ডি',
      joinDate: '২০২৩-০১-১৫',
      totalOrders: 45,
      totalSpent: 125000,
      status: 'সক্রিয়',
      lastOrder: '২০২৪-০১-১০',
      orderHistory: [
        { id: 'ORD2024001', date: '২০২৪-০১-১০', items: 'চাল, ডাল, তেল', amount: 1250, status: 'ডেলিভারি সম্পন্ন' },
        { id: 'ORD2024002', date: '২০২৪-০১-০৫', items: 'সবজি, মাছ', amount: 850, status: 'ডেলিভারি সম্পন্ন' },
        { id: 'ORD2024003', date: '২০২৩-১২-২৮', items: 'ফল, দুধ', amount: 560, status: 'ডেলিভারি সম্পন্ন' }
      ],
      supportHistory: [
        { date: '২০২৪-০১-০৮', issue: 'ডেলিভারি বিলম্ব', status: 'সমাধানকৃত', agent: 'রহিম' },
        { date: '২০২৩-১২-২০', issue: 'পণ্য রিটার্ন', status: 'সমাধানকৃত', agent: 'ফাতেমা' }
      ]
    },
    {
      id: 'CUST002',
      name: 'রহিম উদ্দিন',
      phone: '01712345678',
      email: 'rahim@example.com',
      address: 'বাড়ি নং ৪৫৬, নাসিরাবাদ, চট্টগ্রাম',
      district: 'চট্টগ্রাম',
      upazilla: 'নাসিরাবাদ',
      joinDate: '২০২২-০৮-১০',
      totalOrders: 32,
      totalSpent: 89000,
      status: 'সক্রিয়',
      lastOrder: '২০২৪-০১-০৮',
      orderHistory: [
        { id: 'ORD2024010', date: '২০২৪-০১-০৮', items: 'মসলা, চিনি', amount: 420, status: 'প্রসেসিং' }
      ],
      supportHistory: []
    },
    {
      id: 'CUST003',
      name: 'ফাতেমা খাতুন',
      phone: '01823456789',
      email: 'fatema@example.com',
      address: 'বাড়ি নং ৭৮৯, আগ্রাবাদ, চট্টগ্রাম',
      district: 'চট্টগ্রাম',
      upazilla: 'আগ্রাবাদ',
      joinDate: '২০২৩-০৩-২০',
      totalOrders: 28,
      totalSpent: 67000,
      status: 'সক্রিয়',
      lastOrder: '২০২৪-০১-০৯',
      orderHistory: [
        { id: 'ORD2024015', date: '২০২৪-০১-০৯', items: 'দুধ, ডিম', amount: 320, status: 'ডেলিভারি সম্পন্ন' }
      ],
      supportHistory: [
        { date: '২০২৩-১২-১৫', issue: 'অর্ডার বিলম্ব', status: 'সমাধানকৃত', agent: 'আলী' }
      ]
    },
    {
      id: 'CUST004',
      name: 'করিম মিয়া',
      phone: '01934567890',
      email: 'karim@example.com',
      address: 'বাড়ি নং ৩২১, জিন্দাবাজার, সিলেট',
      district: 'সিলেট',
      upazilla: 'জিন্দাবাজার',
      joinDate: '২০২২-১১-১২',
      totalOrders: 55,
      totalSpent: 145000,
      status: 'সক্রিয়',
      lastOrder: '২০২৪-০১-১১',
      orderHistory: [
        { id: 'ORD2024020', date: '২০২৪-০১-১১', items: 'চা, বিস্কুট', amount: 180, status: 'প্রসেসিং' }
      ],
      supportHistory: []
    },
    {
      id: 'CUST005',
      name: 'সালমা বেগম',
      phone: '01645678901',
      email: 'salma@example.com',
      address: 'বাড়ি নং ৬৫৪, সাপাহার, নওগাঁ',
      district: 'রাজশাহী',
      upazilla: 'সাপাহার',
      joinDate: '২০২৩-০৭-০৮',
      totalOrders: 22,
      totalSpent: 38000,
      status: 'সক্রিয়',
      lastOrder: '২০২৪-০১-০৭',
      orderHistory: [
        { id: 'ORD2024012', date: '২০২৪-০১-০৭', items: 'সবজি বীজ', amount: 450, status: 'ডেলিভারি সম্পন্ন' }
      ],
      supportHistory: []
    }
  ];

  const districts = ['ঢাকা', 'চট্টগ্রাম', 'সিলেট', 'রাজশাহী', 'খুলনা', 'বরিশাল', 'রংপুর', 'ময়মনসিংহ'];
  const upazillas = {
    'ঢাকা': ['ধানমন্ডি', 'গুলশান', 'উত্তরা', 'মিরপুর', 'মতিঝিল'],
    'চট্টগ্রাম': ['নাসিরাবাদ', 'আগ্রাবাদ', 'হালিশহর', 'পাহাড়তলি'],
    'সিলেট': ['জিন্দাবাজার', 'আম্বরখানা', 'সুবিদবাজার']
  };

  const bazars = [
    'দৈনিক বাজার', 'খাদ্য বাজার', 'ওষুধ বাজার', 'জাতীয় বাজার', 'সেবা বাজার', 
    'বই বাজার', 'নার্সারি বাজার', 'ইলেকট্রনিক্স বাজার', 'গার্মেন্টস বাজার'
  ];

  const stores = {
    'দৈনিক বাজার': ['রহিমের দোকান', 'করিমের ভ্যারাইটি স্টোর', 'সালমার কিরানা', 'গ্রামীণ সেলফ স্টোর', 'সকল দোকান'],
    'খাদ্য বাজার': ['আলীর ফুড শপ', 'হালাল ফুড কর্নার', 'গ্রামীণ সেলফ স্টোর', 'সকল দোকান'],
    'ওষুধ বাজার': ['পপুলার ফার্মেসি', 'স্কয়ার ফার্মেসি', 'গ্রামীণ সেলফ স্টোর', 'সকল দোকান'],
    'জাতীয় বাজার': ['জাতীয় স্টোর', 'সেবা স্টোর', 'গ্রামীণ সেলফ স্টোর', 'সকল দোকান'],
    'সেবা বাজার': ['সেবা প্রতিষ্ঠান', 'গ্রামীণ সেলফ স্টোর', 'সকল দোকান'],
    'বই বাজার': ['বই ঘর', 'নলেজ পয়েন্ট', 'গ্রামীণ সেলফ স্টোর', 'সকল দোকান'],
    'নার্সারি বাজার': ['সবুজ নার্সারি', 'ফুলের বাগান', 'গ্রামীণ সেলফ স্টোর', 'সকল দোকান'],
    'ইলেকট্রনিক্স বাজার': ['টেক স্টোর', 'ইলেকট্রনিক্স হাব', 'গ্রামীণ সেলফ স্টোর', 'সকল দোকান'],
    'গার্মেন্টস বাজার': ['ফ্যাশন হাউস', 'পোশাক ঘর', 'গ্রামীণ সেলফ স্টোর', 'সকল দোকান']
  };

  const deliveryPartners = [
    'Grameen Delivery', 'সুন্দরবন কুরিয়ার', 'SA Paribahan', 'পাঠাও কুরিয়ার', 'রেডএক্স'
  ];

  const mockProducts = [
    // Grameen Bazar's own products
    { id: 1, name: 'বাসমতি চাল (১ কেজি)', price: 120, category: 'খাদ্য', vendor: 'grameen', vendorName: 'গ্রামীণ বাজার' },
    { id: 2, name: 'মসুর ডাল (৫০০ গ্রাম)', price: 85, category: 'খাদ্য', vendor: 'grameen', vendorName: 'গ্রামীণ বাজার' },
    { id: 3, name: 'সয়াবিন তেল (১ লিটার)', price: 165, category: 'খাদ্য', vendor: 'grameen', vendorName: 'গ্রামীণ বাজার' },
    { id: 4, name: 'লাল আলু (১ কেজি)', price: 45, category: 'সবজি', vendor: 'grameen', vendorName: 'গ্রামীণ বাজার' },
    { id: 5, name: 'পেঁয়াজ (১ কেজি)', price: 55, category: 'সবজি', vendor: 'grameen', vendorName: 'গ্রামীণ বাজার' },
    { id: 6, name: 'টমেটো (১ কেজি)', price: 60, category: 'সবজি', vendor: 'grameen', vendorName: 'গ্রামীণ বাজার' },
    { id: 7, name: 'দুধ (১ লিটার)', price: 65, category: 'দুগ্ধজাত', vendor: 'grameen', vendorName: 'গ্রামীণ বাজার' },
    { id: 8, name: 'ডিম (১২ পিস)', price: 140, category: 'প্রোটিন', vendor: 'grameen', vendorName: 'গ্রামীণ বাজার' },
    
    // Vendor products
    { id: 9, name: 'আরোরা চাল (১ কেজি)', price: 110, category: 'খাদ্য', vendor: 'vendor', vendorName: 'আরোরা ফুডস' },
    { id: 10, name: 'প্রাণ ডাল (৫০০ গ্রাম)', price: 80, category: 'খাদ্য', vendor: 'vendor', vendorName: 'প্রাণ ফুডস' },
    { id: 11, name: 'রূপচাঁদা তেল (১ লিটার)', price: 160, category: 'খাদ্য', vendor: 'vendor', vendorName: 'রূপচাঁদা অয়েল' },
    { id: 12, name: 'সাদা আলু (১ কেজি)', price: 40, category: 'সবজি', vendor: 'vendor', vendorName: 'ফ্রেশ ভেজিটেবলস' },
    { id: 13, name: 'সবুজ পেঁয়াজ (১ কেজি)', price: 50, category: 'সবজি', vendor: 'vendor', vendorName: 'ফ্রেশ ভেজিটেবলস' },
    { id: 14, name: 'চেরি টমেটো (১ কেজি)', price: 70, category: 'সবজি', vendor: 'vendor', vendorName: 'ফ্রেশ ভেজিটেবলস' },
    { id: 15, name: 'ফার্ম ফ্রেশ দুধ (১ লিটার)', price: 70, category: 'দুগ্ধজাত', vendor: 'vendor', vendorName: 'ফার্ম ফ্রেশ' },
    { id: 16, name: 'ফার্ম ফ্রেশ ডিম (১২ পিস)', price: 150, category: 'প্রোটিন', vendor: 'vendor', vendorName: 'ফার্ম ফ্রেশ' }
  ];

  const filteredProducts = mockProducts.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(productSearchQuery.toLowerCase());
    const matchesVendor = isGrameenProduct ? product.vendor === 'grameen' : product.vendor === 'vendor';
    return matchesSearch && matchesVendor;
  });

  const addToOrder = (product) => {
    const existingItem = orderItems.find(item => item.id === product.id);
    if (existingItem) {
      setOrderItems(orderItems.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setOrderItems([...orderItems, { ...product, quantity: 1 }]);
    }
  };

  const removeFromOrder = (productId) => {
    setOrderItems(orderItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity === 0) {
      removeFromOrder(productId);
    } else {
      setOrderItems(orderItems.map(item => 
        item.id === productId 
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const getTotalAmount = () => {
    return orderItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleOrderTypeSelect = (type) => {
    setSelectedOrderType(type);
    if (type === 'grameen-store') {
      setSelectedBazar('গ্রামীণ সেলফ স্টোর');
      setSelectedStore('গ্রামীণ সেলফ স্টোর');
    } else {
      setSelectedBazar('');
      setSelectedStore('');
    }
  };

  const resetOrderDialog = () => {
    setOrderDialogOpen(false);
    setOrderStep(1);
    setSelectedDistrict('');
    setSelectedUpazilla('');
    setSelectedBazar('');
    setSelectedStore('');
    setSelectedOrderType('');
    setOrderItems([]);
    setProductSearchQuery('');
    setSelectedDeliveryPartner('Grameen Delivery');
    setIsGrameenProduct(true);
    setSelectedPaymentMethod('cod');
    setSelectedDeliveryMethod('home_delivery');
    setDeliveryAddress('');
  };

  const handleNextStep = () => {
    if (orderStep < 3) {
      setOrderStep(orderStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (orderStep > 1) {
      setOrderStep(orderStep - 1);
    }
  };

  const handleCustomerSearch = () => {
    const customer = mockCustomers.find(c => 
      customerSearchType === 'phone' 
        ? c.phone === customerSearchQuery
        : c.name.toLowerCase().includes(customerSearchQuery.toLowerCase())
    );
    
    if (customer) {
      setFoundCustomer(customer);
      setCustomerSearchDialogOpen(false);
      setCustomerProfileDialogOpen(true);
    } else {
      alert('গ্রাহক পাওয়া যায়নি!');
    }
    setCustomerSearchQuery('');
  };

  const handlePlaceOrderForCustomer = () => {
    if (foundCustomer) {
      // Pre-fill customer information
      setSelectedDistrict(foundCustomer.district);
      setSelectedUpazilla(foundCustomer.upazilla);
      setCustomerProfileDialogOpen(false);
      setOrderDialogOpen(true);
    }
  };

  const handleDeliveryStatus = () => {
    setDeliveryStatusDialogOpen(true);
  };

  const handleReturnProcess = () => {
    setReturnProcessDialogOpen(true);
  };

  const handleCallCustomer = () => {
    setPhoneCallDialogOpen(true);
  };

  const resetCustomerSearch = () => {
    setCustomerSearchDialogOpen(false);
    setCustomerProfileDialogOpen(false);
    setFoundCustomer(null);
    setCustomerSearchQuery('');
  };

  const resetAllDialogs = () => {
    setDeliveryStatusDialogOpen(false);
    setReturnProcessDialogOpen(false);
    setPhoneCallDialogOpen(false);
    resetCustomerSearch();
  };

  // Handle customer found during call
  const handleCustomerFoundDuringCall = (customer) => {
    setFoundCustomer(customer);
    setCustomerProfileDialogOpen(true);
  };

  // Phone dialer functions
  const handleDialerDigit = (digit) => {
    if (dialerNumber.length < 15) { // Limit to 15 digits
      setDialerNumber(prev => prev + digit);
    }
  };

  const handleDialerRemove = () => {
    setDialerNumber(prev => prev.slice(0, -1));
  };

  const handleDialerClear = () => {
    setDialerNumber('');
  };

  const handleDialerCall = () => {
    if (dialerNumber.length > 0) {
      // Dispatch outgoing call event
      window.dispatchEvent(new CustomEvent('make-outgoing-call', {
        detail: { number: dialerNumber }
      }));
      setPhoneCallDialogOpen(false);
      setDialerNumber('');
    }
  };

  // Mock data for support dashboard
  const recentTickets = [
    { id: 'T001', customer: 'রহিম উদ্দিন', type: 'অর্ডার প্লেসমেন্ট', priority: 'উচ্চ', status: 'নতুন', time: '৫ মিনিট আগে' },
    { id: 'T002', customer: 'ফাতেমা খাতুন', type: 'রিটার্ন রিকোয়েস্ট', priority: 'মাধ্যম', status: 'প্রসেসিং', time: '১২ মিনিট আগে' },
    { id: 'T003', customer: 'করিম মিয়া', type: 'ডেলিভারি বিলম্ব', priority: 'নিম্ন', status: 'অপেক্ষমান', time: '২৫ মিনিট আগে' },
    { id: 'T004', customer: 'সালমা বেগম', type: 'অর্ডার ক্যান্সেল', priority: 'উচ্চ', status: 'সমাধানকৃত', time: '৩৫ মিনিট আগে' },
    { id: 'T005', customer: 'আব্দুল হক', type: 'পেমেন্ট সমস্যা', priority: 'মাধ্যম', status: 'প্রসেসিং', time: '৪৫ মিনিট আগে' },
  ];

  const issueCategories = [
    { category: 'অর্ডার প্লেসমেন্ট', count: 68, color: '#2196f3' },
    { category: 'রিটার্ন রিকোয়েস্ট', count: 45, color: '#ff9800' },
    { category: 'ডেলিভারি সমস্যা', count: 35, color: '#4caf50' },
    { category: 'পেমেন্ট ইস্যু', count: 28, color: '#f44336' },
    { category: 'প্রোডাক্ট ইনকোয়ারি', count: 22, color: '#9c27b0' },
    { category: 'অ্যাকাউন্ট সমস্যা', count: 12, color: '#ff5722' },
  ];

  const urgentOrders = [
    { 
      id: 'ORD001', 
      customer: 'মোহাম্মদ আলী', 
      phone: '০১৭১২৩৪৫৬৭৮',
      issue: 'জরুরি অর্ডার - ওষুধ প্রয়োজন', 
      time: '২ মিনিট আগে',
      location: 'ঢাকা, ধানমন্ডি'
    },
    { 
      id: 'ORD002', 
      customer: 'নাসির উদ্দিন', 
      phone: '০১৮৯৮৭৬৫৪৩২',
      issue: 'দৈনিক বাজার করতে চান', 
      time: '৮ মিনিট আগে',
      location: 'চট্টগ্রাম, নাসিরাবাদ'
    },
    { 
      id: 'ORD003', 
      customer: 'সাকিনা খাতুন', 
      phone: '০১৯১২৩৪৫৬৭৮',
      issue: 'শিশুর খাবার অর্ডার', 
      time: '১৫ মিনিট আগে',
      location: 'সিলেট, জিন্দাবাজার'
    },
  ];

  const recentCustomerOrders = [
    { id: 'O001', customer: 'রহিম উদ্দিন', items: 'চাল, ডাল, তেল', amount: '৳১,২৫০', status: 'ডেলিভারি সম্পন্ন', date: 'আজ' },
    { id: 'O002', customer: 'ফাতেমা খাতুন', items: 'সবজি, মাছ', amount: '৳৮৫০', status: 'প্রসেসিং', date: 'গতকাল' },
    { id: 'O003', customer: 'করিম মিয়া', items: 'ফল, দুধ', amount: '৳৫৬০', status: 'শিপ করা হয়েছে', date: 'গতকাল' },
    { id: 'O004', customer: 'সালমা বেগম', items: 'মসলা, চিনি', amount: '৳৪২০', status: 'অপেক্ষমান', date: '২ দিন আগে' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Asterisk Call Manager */}
      <AsteriskCallManager 
        customerDatabase={mockCustomers}
        onCustomerFound={handleCustomerFoundDuringCall}
      />

      <AppBarStyled>
        <Toolbar>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {t.title}
          </Typography>
          
          {/* Search Field - Hidden on Mobile */}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <TextField
              size="small"
              placeholder="টিকেট বা গ্রাহক খুঁজুন..."
              sx={{
                mr: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255,255,255,0.7)',
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          {/* Language Switcher */}
          <IconButton 
            color="inherit" 
            onClick={handleLanguageMenuOpen}
            sx={{ mr: 1 }}
          >
            <LanguageIcon />
          </IconButton>
          
          <IconButton color="inherit">
            <Badge badgeContent={15} color="secondary">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>S</Avatar>
          </IconButton>
          
          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            disableScrollLock={true}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {t.profile}
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              {t.settings}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t.logout}
            </MenuItem>
          </Menu>
          
          {/* Language Menu */}
          <Menu
            anchorEl={languageMenuAnchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(languageMenuAnchor)}
            onClose={handleLanguageMenuClose}
            disableScrollLock={true}
          >
            <MenuItem 
              onClick={() => handleLanguageChange('bn')}
              selected={language === 'bn'}
            >
              {t.bangla}
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={language === 'en'}
            >
              {t.english}
            </MenuItem>
          </Menu>
          
          {/* Support Dropdown Menu */}
          <Menu
            anchorEl={supportMenuAnchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            open={Boolean(supportMenuAnchor)}
            onClose={handleSupportMenuClose}
            disableScrollLock={true}
            MenuListProps={{
              onMouseEnter: handleDropdownEnter,
              onMouseLeave: handleDropdownLeave,
            }}
            sx={{
              mt: 1,
              '& .MuiPaper-root': {
                backgroundColor: 'white',
                minWidth: 200,
              }
            }}
          >
            {supportMenuItems.map((item) => (
              <MenuItem 
                key={item.text}
                onClick={() => handleSupportNavigation(item.path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
        
        {/* Navigation Tabs - Hidden on Mobile */}
        {!isMobile && (
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'rgba(255,255,255,0.2)',
            background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)'
          }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              textColor="inherit"
              TabIndicatorProps={{
                style: { backgroundColor: 'white' }
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.8)',
                  '&.Mui-selected': {
                    color: 'white'
                  }
                }
              }}
            >
              {menuItems.map((item, index) => (
                <Tab 
                  key={item.text}
                  label={item.text} 
                  icon={item.icon}
                  iconPosition="start"
                  sx={{ minWidth: 120 }}
                  onMouseEnter={item.hasDropdown ? handleSupportMenuEnter : undefined}
                  onMouseLeave={item.hasDropdown ? handleSupportMenuLeave : undefined}
                />
              ))}
            </Tabs>
          </Box>
        )}
              </AppBarStyled>

        {/* Mobile Navigation Drawer */}
        <SwipeableDrawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={handleMobileDrawerClose}
          onOpen={() => setMobileDrawerOpen(true)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              backgroundColor: 'white',
            },
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {t.title}
              </Typography>
              <IconButton onClick={handleMobileDrawerClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          <List component="nav" sx={{ p: 1 }}>
            {menuItems.map((item, index) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (item.hasDropdown) {
                      // For support menu, open the first item
                      handleMobileNavigation('/support/dashboard/support/customer-queries');
                    } else {
                      handleMobileNavigation(item.path);
                    }
                  }}
                  selected={location.pathname === item.path || (item.hasDropdown && location.pathname.includes('/support'))}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                  {item.hasDropdown && (
                    <Badge badgeContent={supportMenuItems.length} color="primary" />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Support Submenu in Mobile Drawer */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
              {t.support}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {supportMenuItems.map((item) => (
                <Button
                  key={item.text}
                  variant="outlined"
                  size="small"
                  startIcon={item.icon}
                  fullWidth
                  component={Link}
                  to={item.path}
                  onClick={handleMobileDrawerClose}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Quick Actions in Mobile Drawer */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
              দ্রুত কার্যক্রম
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<ShoppingCartIcon />}
                fullWidth
                onClick={() => {
                  setOrderDialogOpen(true);
                  handleMobileDrawerClose();
                }}
                sx={{ justifyContent: 'flex-start' }}
              >
                গ্রাহকের জন্য অর্ডার করুন
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonSearchIcon />}
                fullWidth
                onClick={() => {
                  setCustomerSearchDialogOpen(true);
                  handleMobileDrawerClose();
                }}
                sx={{ justifyContent: 'flex-start' }}
              >
                গ্রাহক খুঁজুন
              </Button>
            </Box>
          </Box>
        </SwipeableDrawer>

        {/* Mobile Floating Action Button */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="menu"
            onClick={handleMobileDrawerToggle}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1000,
              display: { xs: 'flex', md: 'none' },
              background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)'
            }}
          >
            <MenuIcon />
          </Fab>
        )}
        
        {/* Main Content Area with Sidebar */}
        <Box sx={{ display: 'flex', pt: { xs: 8, md: 16 } }}>
        {/* Left Sidebar - Hidden on Mobile */}
        {!isMobile && (
          <Box
            sx={{
              width: 280,
              backgroundColor: 'white',
              borderRight: '1px solid #e0e0e0',
              minHeight: 'calc(100vh - 128px)',
              p: 2,
              position: 'sticky',
              top: 128,
            }}
          >
          {/* Quick Actions */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #2e7d32 0%, #1976d2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            দ্রুত কার্যক্রম
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<ShoppingCartIcon />}
              fullWidth
              sx={{ justifyContent: 'flex-start' }}
              color="primary"
              onClick={() => setOrderDialogOpen(true)}
            >
              গ্রাহকের জন্য অর্ডার করুন
            </Button>
            
            <Button
              variant="contained"
              startIcon={<PersonSearchIcon />}
              fullWidth
              sx={{ justifyContent: 'flex-start' }}
              color="success"
              onClick={() => setCustomerSearchDialogOpen(true)}
            >
              গ্রাহক খুঁজুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<QuestionAnswerIcon />}
              fullWidth
              component={Link}
              to="/support/dashboard/support/customer-queries"
              sx={{ justifyContent: 'flex-start' }}
            >
              নতুন টিকেট
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<PhoneIcon />}
              fullWidth
              sx={{ justifyContent: 'flex-start' }}
              onClick={() => {
                // Simulate an incoming call for testing
                window.dispatchEvent(new CustomEvent('test-incoming-call', {
                  detail: {
                    callId: 'test-' + Date.now(),
                    callerNumber: '01712345678',
                    callerName: 'টেস্ট গ্রাহক',
                    callerLocation: 'ঢাকা, বাংলাদেশ'
                  }
                }));
              }}
            >
              টেস্ট কল (ডেমো)
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<PhoneIcon />}
              fullWidth
              sx={{ justifyContent: 'flex-start' }}
              onClick={handleCallCustomer}
            >
              ফোন সাপোর্ট
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              fullWidth
              sx={{ justifyContent: 'flex-start' }}
              onClick={handleReturnProcess}
            >
              রিটার্ন প্রসেস
            </Button>
          </Box>
          
          {/* Today's Stats */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            আজকের পরিসংখ্যান
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 2, bgcolor: '#e8f5e8' }}>
              <Typography variant="body2" color="text.secondary">
                সমাধানকৃত টিকেট
              </Typography>
              <Typography variant="h4" color="success.main">
                ২৮
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
              <Typography variant="body2" color="text.secondary">
                পেন্ডিং টিকেট
              </Typography>
              <Typography variant="h4" color="warning.main">
                ১৫
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f3e5f5' }}>
              <Typography variant="body2" color="text.secondary">
                নতুন টিকেট
              </Typography>
              <Typography variant="h4" color="primary.main">
                ১২
              </Typography>
            </Paper>
          </Box>
          
          {/* Recent Notifications */}
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 3, 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            সাম্প্রতিক সতর্কতা
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Paper sx={{ p: 2, bgcolor: '#ffebee' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                জরুরি টিকেট
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ১ মিনিট আগে
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                পেমেন্ট ইস্যু
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ৮ মিনিট আগে
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ডেলিভারি বিলম্ব
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ১২ মিনিট আগে
              </Typography>
            </Paper>
          </Box>
        </Box>
        )}
        
        {/* Main Content */}
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            minHeight: 'calc(100vh - 128px)',
            p: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          <Routes>
            <Route path="/" element={
              <DashboardHome 
                orderDialogOpen={orderDialogOpen}
                setOrderDialogOpen={setOrderDialogOpen}
                customerSearchDialogOpen={customerSearchDialogOpen}
                setCustomerSearchDialogOpen={setCustomerSearchDialogOpen}
                mockCustomers={mockCustomers}
                recentTickets={recentTickets}
                issueCategories={issueCategories}
                urgentOrders={urgentOrders}
                recentCustomerOrders={recentCustomerOrders}
              />
            } />
            <Route path="/support/customer-queries" element={<CustomerQueries />} />
            <Route path="/support/orders" element={<OrderSupport />} />
            <Route path="/support/voice-order" element={<VoiceOrder />} />
            <Route path="/support/technical" element={<TechnicalSupport />} />
            <Route path="/support/delivery" element={<DeliverySupport />} />
            <Route path="/support/payment" element={<PaymentSupport />} />
            <Route path="/tickets" element={<SupportTickets />} />
            <Route path="/analytics" element={<SupportAnalytics />} />
            <Route path="/settings" element={<SupportSettings />} />
          </Routes>
        </Box>
      </Box>

      {/* Phone Support Dialog */}
      <Dialog 
        open={phoneCallDialogOpen} 
        onClose={() => setPhoneCallDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
        disableScrollLock={true}
      >
        <DialogTitle sx={{ 
          bgcolor: '#1976d2', 
          color: 'white',
          textAlign: 'center',
          pb: 1
        }}>
          <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          ফোন ডায়ালার
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* Number Display */}
          <Box sx={{ 
            p: 3, 
            textAlign: 'center',
            bgcolor: '#f8f9fa',
            minHeight: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontFamily: 'monospace',
                fontWeight: 400,
                color: dialerNumber ? '#1976d2' : '#bbb',
                letterSpacing: 2
              }}
            >
              {dialerNumber || 'নম্বর টাইপ করুন'}
            </Typography>
          </Box>

          {/* Dialer Buttons Grid */}
          <Box sx={{ p: 2 }}>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {/* Row 1: 1, 2, 3 */}
              {['1', '2', '3'].map((digit) => (
                <Grid item xs={4} key={digit}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => handleDialerDigit(digit)}
                    sx={{
                      height: 60,
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      borderColor: '#e0e0e0',
                      color: '#333',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                        borderColor: '#1976d2'
                      }
                    }}
                  >
                    {digit}
                  </Button>
                </Grid>
              ))}

              {/* Row 2: 4, 5, 6 */}
              {['4', '5', '6'].map((digit) => (
                <Grid item xs={4} key={digit}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => handleDialerDigit(digit)}
                    sx={{
                      height: 60,
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      borderColor: '#e0e0e0',
                      color: '#333',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                        borderColor: '#1976d2'
                      }
                    }}
                  >
                    {digit}
                  </Button>
                </Grid>
              ))}

              {/* Row 3: 7, 8, 9 */}
              {['7', '8', '9'].map((digit) => (
                <Grid item xs={4} key={digit}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => handleDialerDigit(digit)}
                    sx={{
                      height: 60,
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      borderColor: '#e0e0e0',
                      color: '#333',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                        borderColor: '#1976d2'
                      }
                    }}
                  >
                    {digit}
                  </Button>
                </Grid>
              ))}

              {/* Row 4: *, 0, # */}
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => handleDialerDigit('*')}
                  sx={{
                    height: 60,
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    borderColor: '#e0e0e0',
                    color: '#333',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                      borderColor: '#1976d2'
                    }
                  }}
                >
                  *
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => handleDialerDigit('0')}
                  sx={{
                    height: 60,
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    borderColor: '#e0e0e0',
                    color: '#333',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                      borderColor: '#1976d2'
                    }
                  }}
                >
                  0
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => handleDialerDigit('#')}
                  sx={{
                    height: 60,
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    borderColor: '#e0e0e0',
                    color: '#333',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                      borderColor: '#1976d2'
                    }
                  }}
                >
                  #
                </Button>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Grid container spacing={1}>
              {/* Remove/Backspace Button */}
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={handleDialerRemove}
                  disabled={!dialerNumber}
                  sx={{
                    height: 50,
                    borderColor: '#e0e0e0',
                    color: '#666',
                    '&:hover': {
                      bgcolor: '#ffebee',
                      borderColor: '#f44336'
                    },
                    '&:disabled': {
                      opacity: 0.3
                    }
                  }}
                >
                  <BackspaceIcon />
                </Button>
              </Grid>

              {/* Call Button */}
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleDialerCall}
                  disabled={!dialerNumber}
                  sx={{
                    height: 50,
                    bgcolor: '#4caf50',
                    '&:hover': {
                      bgcolor: '#388e3c'
                    },
                    '&:disabled': {
                      opacity: 0.3
                    }
                  }}
                >
                  <PhoneIcon />
                </Button>
              </Grid>

              {/* Clear Button */}
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={handleDialerClear}
                  disabled={!dialerNumber}
                  sx={{
                    height: 50,
                    borderColor: '#e0e0e0',
                    color: '#666',
                    '&:hover': {
                      bgcolor: '#ffebee',
                      borderColor: '#f44336'
                    },
                    '&:disabled': {
                      opacity: 0.3
                    }
                  }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Quick Dial Suggestions */}
          <Box sx={{ p: 2, pt: 0 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              দ্রুত ডায়াল:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['017', '018', '019', '016', '013', '014', '015'].map((prefix) => (
                <Chip
                  key={prefix}
                  label={prefix}
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={() => {
                    if (dialerNumber.length === 0) {
                      setDialerNumber(prefix);
                    }
                  }}
                  sx={{
                    '&:hover': {
                      bgcolor: '#e3f2fd'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => {
            setPhoneCallDialogOpen(false);
            setDialerNumber('');
          }}>
            বন্ধ করুন
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Process Dialog */}
      <Dialog 
        open={returnProcessDialogOpen} 
        onClose={() => setReturnProcessDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        disableScrollLock={true}
      >
        <DialogTitle sx={{ bgcolor: '#ff9800', color: 'white' }}>
          রিটার্ন প্রসেস
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" gutterBottom>
            পণ্য রিটার্ন প্রক্রিয়া শুরু করুন:
          </Typography>
          
          <TextField
            margin="dense"
            label="অর্ডার ID"
            fullWidth
            variant="outlined"
            placeholder="ORD2024001"
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="গ্রাহকের ফোন নম্বর"
            fullWidth
            variant="outlined"
            placeholder="01XXXXXXXXX"
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="রিটার্নের কারণ"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="পণ্য রিটার্নের কারণ লিখুন..."
            sx={{ mb: 2 }}
          />

          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              রিটার্ন নীতি:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • পণ্য ক্রয়ের ৭ দিনের মধ্যে রিটার্ন করা যাবে<br/>
              • পণ্যটি অবশ্যই মূল অবস্থায় থাকতে হবে<br/>
              • ক্রয়ের রসিদ প্রয়োজন হবে
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnProcessDialogOpen(false)}>বাতিল</Button>
          <Button 
            variant="contained" 
            color="warning"
            startIcon={<AssignmentIcon />}
          >
            রিটার্ন প্রক্রিয়া শুরু করুন
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Order Dialog */}
      <Dialog 
        open={orderDialogOpen} 
        onClose={() => setOrderDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
            color: 'white',
            borderRadius: '8px 8px 0 0',
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              গ্রাহকের জন্য অর্ডার প্লেস করুন
            </Typography>
            <IconButton 
              onClick={() => setOrderDialogOpen(false)}
              sx={{ color: 'white' }}
            >
              <ExitToAppIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={3}>
              {/* Left Column - Product Search and Product Options */}
              <Grid item xs={12} md={6}>
                {/* Search and Vendor Switch Section */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="পণ্য খুঁজুন..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#5e35b1' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#5e35b1',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#5e35b1',
                        },
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isGrameenProduct}
                        onChange={(e) => setIsGrameenProduct(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#5e35b1',
                            '&:hover': {
                              backgroundColor: 'rgba(94, 53, 177, 0.08)',
                            },
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#5e35b1',
                          },
                        }}
                      />
                    }
                    label={isGrameenProduct ? "গ্রামীণ বাজারের পণ্য" : "ভেন্ডরের পণ্য"}
                    sx={{ minWidth: 200 }}
                  />
                </Box>

                {/* Products Grid */}
                <Grid container spacing={2}>
                  {filteredProducts.map((product) => (
                    <Grid item xs={12} sm={6} key={product.id}>
                      <Card sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 16px rgba(94, 53, 177, 0.2)',
                          transform: 'translateY(-2px)',
                        }
                      }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="div" gutterBottom sx={{ color: '#2c3e50' }}>
                            {product.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {product.category} • {product.vendorName}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5e35b1' }}>
                            ৳{product.price}
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => addToOrder(product)}
                            fullWidth
                            sx={{
                              background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
                              '&:hover': {
                                background: 'linear-gradient(45deg, #4a148c 30%, #1565c0 90%)',
                              },
                            }}
                          >
                            অর্ডারে যোগ করুন
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Right Column - Order Summary and Payment Options */}
              <Grid item xs={12} md={6}>
                {/* Order Summary */}
                {orderItems.length > 0 && (
                  <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, mb: 3, border: '1px solid #e9ecef' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#2c3e50', fontWeight: 600 }}>
                      অর্ডার সামারি
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: '#5e35b1' }}>পণ্য</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#5e35b1' }}>মূল্য</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, color: '#5e35b1' }}>পরিমাণ</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#5e35b1' }}>মোট</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, color: '#5e35b1' }}>অ্যাকশন</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orderItems.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell align="right">৳{item.price}</TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                    sx={{ color: '#5e35b1' }}
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                  <Typography sx={{ fontWeight: 600 }}>{item.quantity}</Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                    sx={{ color: '#5e35b1' }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>৳{item.price * item.quantity}</TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeFromOrder(item.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#5e35b1' }}>
                        মোট: ৳{getTotalAmount()}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Delivery and Payment Options */}
                {orderItems.length > 0 && (
                  <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, mb: 3, border: '1px solid #e9ecef' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#2c3e50', fontWeight: 600 }}>
                      ডেলিভারি এবং পেমেন্ট অপশন
                    </Typography>
                    
                    {/* Delivery Method */}
                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#5e35b1' }}>
                        ডেলিভারি পদ্ধতি
                      </Typography>
                      <RadioGroup
                        value={selectedDeliveryMethod}
                        onChange={(e) => setSelectedDeliveryMethod(e.target.value)}
                      >
                        <FormControlLabel 
                          value="home_delivery" 
                          control={<Radio sx={{ color: '#5e35b1', '&.Mui-checked': { color: '#5e35b1' } }} />} 
                          label="বাড়িতে ডেলিভারি" 
                        />
                        <FormControlLabel 
                          value="pickup" 
                          control={<Radio sx={{ color: '#5e35b1', '&.Mui-checked': { color: '#5e35b1' } }} />} 
                          label="পিকআপ" 
                        />
                      </RadioGroup>
                    </FormControl>

                    {/* Payment Method */}
                    <FormControl component="fieldset">
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#5e35b1' }}>
                        পেমেন্ট পদ্ধতি
                      </Typography>
                      <RadioGroup
                        value={selectedPaymentMethod}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      >
                        <FormControlLabel 
                          value="cod" 
                          control={<Radio sx={{ color: '#5e35b1', '&.Mui-checked': { color: '#5e35b1' } }} />} 
                          label="ক্যাশ অন ডেলিভারি (COD)" 
                        />
                        <FormControlLabel 
                          value="online" 
                          control={<Radio sx={{ color: '#5e35b1', '&.Mui-checked': { color: '#5e35b1' } }} />} 
                          label="অনলাইন পেমেন্ট" 
                        />
                        <FormControlLabel 
                          value="bkash" 
                          control={<Radio sx={{ color: '#5e35b1', '&.Mui-checked': { color: '#5e35b1' } }} />} 
                          label="বিকাশ" 
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                )}

                {/* Address Input Field */}
                {orderItems.length > 0 && (
                  <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, mb: 3, border: '1px solid #e9ecef' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#2c3e50', fontWeight: 600 }}>
                      ডেলিভারি ঠিকানা
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      placeholder="ডেলিভারি ঠিকানা লিখুন..."
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#5e35b1',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#5e35b1',
                          },
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Empty state when no items */}
                {orderItems.length === 0 && (
                  <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      কোন পণ্য নির্বাচন করা হয়নি
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      বাম দিক থেকে পণ্য নির্বাচন করুন
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setOrderDialogOpen(false)}
            sx={{ 
              color: '#5e35b1',
              borderColor: '#5e35b1',
              '&:hover': {
                borderColor: '#4a148c',
                backgroundColor: 'rgba(94, 53, 177, 0.04)',
              }
            }}
            variant="outlined"
          >
            বাতিল
          </Button>
          {orderItems.length > 0 && (
            <Button 
              variant="contained" 
              size="large"
              startIcon={<ShoppingCartIcon />}
              disabled={!deliveryAddress.trim()}
              sx={{
                background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #4a148c 30%, #1565c0 90%)',
                },
                '&:disabled': {
                  background: '#e0e0e0',
                  color: '#9e9e9e',
                },
              }}
            >
              অর্ডার প্লেস করুন (৳{getTotalAmount()})
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Customer Search Dialog - Simple version for now */}
      <Dialog 
        open={customerSearchDialogOpen} 
        onClose={() => setCustomerSearchDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableScrollLock={true}
      >
        <DialogTitle>গ্রাহক খুঁজুন</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="ফোন নম্বর বা নাম লিখুন"
            fullWidth
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerSearchDialogOpen(false)}>বাতিল</Button>
          <Button variant="contained">খুঁজুন</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Default support dashboard home component
const DashboardHome = ({ 
  orderDialogOpen, 
  setOrderDialogOpen,
  customerSearchDialogOpen,
  setCustomerSearchDialogOpen,
  mockCustomers,
  recentTickets,
  issueCategories,
  urgentOrders,
  recentCustomerOrders 
}) => {
  
  return (
    <Box>
      {/* Quick Order Placement Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          {/* Quick Order Widget */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)', color: 'white' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                দ্রুত অর্ডার প্লেসমেন্ট
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ShoppingCartIcon />}
                  onClick={() => setOrderDialogOpen(true)}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    color: 'white',
                    fontWeight: 600
                  }}
                >
                  গ্রাহকের জন্য অর্ডার করুন
                </Button>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  ফোনে গ্রাহকের সাথে কথা বলে সরাসরি অর্ডার প্লেস করুন
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Customer Search Widget */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)', color: 'white' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                গ্রাহক খোঁজা
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PersonSearchIcon />}
                  onClick={() => setCustomerSearchDialogOpen(true)}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    color: 'white',
                    fontWeight: 600
                  }}
                >
                  গ্রাহক খুঁজুন
                </Button>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  ফোন নম্বর বা নাম দিয়ে গ্রাহকের তথ্য খুঁজুন
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="rgba(255,255,255,0.8)" gutterBottom>
                    আজকের টিকেট
                  </Typography>
                  <Typography variant="h4" component="div">
                    ২৮
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PendingActionsIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="rgba(255,255,255,0.8)" gutterBottom>
                    পেন্ডিং টিকেট
                  </Typography>
                  <Typography variant="h4" component="div">
                    ১৫
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="rgba(255,255,255,0.8)" gutterBottom>
                    সমাধানকৃত
                  </Typography>
                  <Typography variant="h4" component="div">
                    ৪২
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StarIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="rgba(255,255,255,0.8)" gutterBottom>
                    গড় রেটিং
                  </Typography>
                  <Typography variant="h4" component="div">
                    ৪.৮
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Tickets */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              সাম্প্রতিক টিকেট
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>টিকেট ID</TableCell>
                    <TableCell>গ্রাহক</TableCell>
                    <TableCell>ধরন</TableCell>
                    <TableCell>অগ্রাধিকার</TableCell>
                    <TableCell>স্ট্যাটাস</TableCell>
                    <TableCell>সময়</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTickets.map((ticket) => (
                    <TableRow key={ticket.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{ticket.id}</TableCell>
                      <TableCell>{ticket.customer}</TableCell>
                      <TableCell>{ticket.type}</TableCell>
                      <TableCell>
                        <Chip 
                          label={ticket.priority} 
                          size="small"
                          color={
                            ticket.priority === 'উচ্চ' ? 'error' : 
                            ticket.priority === 'মাধ্যম' ? 'warning' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={ticket.status} 
                          size="small"
                          color={
                            ticket.status === 'নতুন' ? 'primary' : 
                            ticket.status === 'প্রসেসিং' ? 'warning' : 
                            ticket.status === 'সমাধানকৃত' ? 'success' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{ticket.time}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Urgent Orders */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#d32f2f' }}>
              জরুরি অর্ডার
            </Typography>
            <List>
              {urgentOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#f44336', width: 32, height: 32 }}>
                        <PhoneIcon fontSize="small" />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {order.customer}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.phone}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {order.issue}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.location} • {order.time}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
            <Button 
              fullWidth 
              variant="outlined" 
              color="error" 
              sx={{ mt: 2 }}
            >
              সব জরুরি অর্ডার দেখুন
            </Button>
          </Paper>
        </Grid>

        {/* Issue Categories */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              সমস্যার ধরন
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {issueCategories.map((category) => (
                <Box key={category.category} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: category.color 
                      }} 
                    />
                    <Typography variant="body2">{category.category}</Typography>
                  </Box>
                  <Chip label={category.count} size="small" />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Customer Orders */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              সাম্প্রতিক অর্ডার
            </Typography>
            <List>
              {recentCustomerOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#2196f3', width: 32, height: 32 }}>
                        <ShoppingBasketIcon fontSize="small" />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {order.customer}
                          </Typography>
                          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                            {order.amount}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {order.items}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                            <Chip 
                              label={order.status} 
                              size="small" 
                              color={
                                order.status === 'ডেলিভারি সম্পন্ন' ? 'success' :
                                order.status === 'প্রসেসিং' ? 'warning' :
                                order.status === 'শিপ করা হয়েছে' ? 'info' : 'default'
                              }
                            />
                            <Typography variant="caption" color="text.secondary">
                              {order.date}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Order Dialog - Simple version for now */}
      <Dialog 
        open={orderDialogOpen} 
        onClose={() => setOrderDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        disableScrollLock={true}
      >
        <DialogTitle>গ্রাহকের জন্য অর্ডার প্লেস করুন</DialogTitle>
        <DialogContent>
          <Typography>অর্ডার প্লেসমেন্ট ফর্ম এখানে থাকবে</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>বাতিল</Button>
          <Button variant="contained">অর্ডার প্লেস করুন</Button>
        </DialogActions>
      </Dialog>

      {/* Customer Search Dialog - Simple version for now */}
      <Dialog 
        open={customerSearchDialogOpen} 
        onClose={() => setCustomerSearchDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableScrollLock={true}
      >
        <DialogTitle>গ্রাহক খুঁজুন</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="ফোন নম্বর বা নাম লিখুন"
            fullWidth
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerSearchDialogOpen(false)}>বাতিল</Button>
          <Button variant="contained">খুঁজুন</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export { DashboardHome as default, SupportDashboard };
